import useSymbol from '@/hooks/use-symbol';
import { useMarketStore } from '@/store/use-markets-store';
import {
  bigIntToDecimalStr,
  bigIntToNum,
  formatNumber,
} from '@/utils/value-format';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import OrderBookRow, { OrderBookBar } from './order-book-row';

type Props = {
  unit: string;
  tickSize: string;
};

const ROW_HEIGHT = 20;
const ROW_SPACING = 3;
const MIDDLE_ROW_HEIGHT = 26;

const getNumberOfRows = (wrapper: HTMLDivElement, headerHeight: number) => {
  const { clientHeight } = wrapper;
  const tBodyHeight =
    clientHeight - MIDDLE_ROW_HEIGHT - headerHeight - ROW_SPACING;
  const numberOfRows = tBodyHeight / 2 / (ROW_HEIGHT + ROW_SPACING);
  return Math.floor(numberOfRows);
};

export default function OrderBook({ unit, tickSize }: Props) {
  const { t } = useTranslation();
  const tableRef = useRef<HTMLTableElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLTableSectionElement>(null);

  const symbol = useSymbol();

  const [numberOfRows, setNumberOfRows] = useState(0);

  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const wrapper = wrapperRef.current;
    setNumberOfRows(getNumberOfRows(wrapper, headerHeight));
    const observer = new ResizeObserver(() =>
      setNumberOfRows(getNumberOfRows(wrapper, headerHeight)),
    );
    observer.observe(wrapper);
    return () => {
      observer.unobserve(wrapper);
    };
  }, [headerHeight]);

  useEffect(() => {
    if (!headerRef.current) return;
    const header = headerRef.current;
    setHeaderHeight(header.clientHeight);
    const observer = new ResizeObserver(() =>
      setHeaderHeight(header.clientHeight),
    );
    observer.observe(header);
    return () => {
      observer.unobserve(header);
    };
  }, []);

  const asksLength = useStore(useMarketStore(symbol), (state) =>
    Math.min(
      (tickSize in state.books ? state.books[tickSize] : { bids: [], asks: [] })
        .asks.length,
      numberOfRows,
    ),
  );
  const hasRows = useStore(
    useMarketStore(symbol),
    (state) =>
      asksLength > 0 ||
      (tickSize in state.books ? state.books[tickSize] : { bids: [], asks: [] })
        .bids.length > 0,
  );

  if (!hasRows) {
    return (
      <div ref={wrapperRef} className="absolute inset-0 h-full w-full">
        <div className="relative flex h-full flex-col">
          <table className="mt-1 w-full border-separate border-spacing-y-1">
            <thead ref={headerRef}>
              <tr className="[&>th:first-child]:pl-4 [&>th:first-child]:text-left [&>th:last-child]:pr-4 [&>th]:pb-1 [&>th]:text-right [&>th]:align-bottom [&>th]:font-normal [&>th]:text-vestgrey-100">
                <th>{t('orderBook.price')}</th>
                <th>
                  {t('orderBook.size')} ({unit})
                </th>
                <th>
                  {t('orderBook.total')} ({unit})
                </th>
              </tr>
            </thead>
          </table>
          <div className="flex flex-1 items-center justify-center"></div>
        </div>
      </div>
    );
  }

  const decimals = tickSize.includes('.') ? tickSize.split('.')[1].length : 0;

  return (
    //   <ScrollArea className="h-full">
    <div ref={wrapperRef} className="absolute inset-0 h-full w-full">
      <div className="relative">
        <table
          ref={tableRef}
          style={{ borderSpacing: `0px ${ROW_SPACING}px` }}
          className="relative z-10 w-full border-separate"
        >
          <thead ref={headerRef}>
            <tr className="[&>th:first-child]:pl-4 [&>th:first-child]:text-left [&>th:last-child]:pr-4 [&>th]:sticky [&>th]:top-1 [&>th]:z-20 [&>th]:bg-background [&>th]:text-right [&>th]:align-bottom [&>th]:font-normal [&>th]:text-vestgrey-100">
              <th>{t('orderBook.price')}</th>
              <th>
                {t('orderBook.size')} ({unit})
              </th>
              <th>
                {t('orderBook.total')} ({unit})
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: numberOfRows }, (_, i) => (
              <OrderBookRow
                unit={unit}
                decimals={decimals}
                index={numberOfRows - 1 - i}
                type="ask"
                key={`ask-${i}`}
                tickSize={tickSize}
              />
            ))}
            <tr>
              <td
                className="border-y border-border bg-vestgrey-800 px-4 py-1"
                colSpan={3}
              >
                <Spread tickSize={tickSize} />
              </td>
            </tr>
            {Array.from({ length: numberOfRows }, (_, i) => (
              <OrderBookRow
                unit={unit}
                decimals={decimals}
                index={i}
                type="bid"
                key={`bid-${i}`}
                tickSize={tickSize}
              />
            ))}
          </tbody>
        </table>

        <div
          style={{ top: headerHeight + 6 }}
          className="absolute flex w-full flex-col gap-1"
        >
          {Array.from({ length: numberOfRows }, (_, i) => (
            <OrderBookBar
              numberOfRows={numberOfRows}
              index={numberOfRows - 1 - i}
              type="ask"
              key={`ask-bar-${i}`}
              tickSize={tickSize}
            />
          ))}
        </div>
        <div
          style={{
            top: 23 * asksLength + headerHeight + 36,
          }}
          className="absolute flex w-full flex-col gap-1"
        >
          {Array.from({ length: numberOfRows }, (_, i) => (
            <OrderBookBar
              numberOfRows={numberOfRows}
              index={i}
              type="bid"
              key={`bid-bar-${i}`}
              tickSize={tickSize}
            />
          ))}
        </div>
      </div>
    </div>
    //   </ScrollArea>
  );
}

function Spread({ tickSize }: { tickSize: string }) {
  const symbol = useSymbol();
  const { t } = useTranslation();

  const spread = useStore(useMarketStore(symbol), (state) => {
    const { bids, asks } =
      tickSize in state.books ? state.books[tickSize] : { bids: [], asks: [] };

    if (asks.length > 0 && bids.length > 0) {
      const [lowestAskPrice] = asks[0];
      const [highestBidPrice] = bids[0];
      return lowestAskPrice - highestBidPrice;
    }
    return 0n;
  });

  const midPrice = useStore(
    useMarketStore(symbol),
    (state) => state.marketData.midPrice,
  );
  const priceDecimals = useStore(
    useMarketStore(symbol),
    (state) => state.marketSpec.priceDecimals,
  );

  return (
    <div className="flex items-center justify-center gap-6">
      <span>{t('orderBook.spread')}</span>
      <span className="font-mono font-medium">
        {bigIntToDecimalStr(spread, priceDecimals)}
      </span>
      <span className="font-mono font-medium">
        {midPrice > 0n
          ? formatNumber(
              (100 * bigIntToNum(spread, priceDecimals)) /
                bigIntToNum(midPrice, priceDecimals),
              { maxDigits: 3, style: 'percent', round: 'floor' },
            )
          : '-'}
      </span>
    </div>
  );
}
